// [Codex 2026-07-17] Normal assistant-ui chat is Fintheon-owned and fixed to
// the selected governed provider route and durable-run contract.
import { useFintheonChat } from "./hooks/useFintheonChat";
import type { FintheonWorkspaceContext } from "./hooks/useFintheonChat";
import { useOpenAgentsRuntime } from "./hooks/useOpenAgentsRuntime";
import { usePersistentFintheonConversation } from "../../hooks/usePersistentFintheonConversation";
import { toFintheonAgentOverride } from "../../lib/fintheonAgentRouting";
import type { ReasoningLevel } from "./reasoning";
import type { AgenticTaskMode } from "../../lib/agentic/task-mode";

// [claude-code 2026-03-09] Added surfaceId for per-surface session isolation
export function useFintheonRuntime(
  agentId: string,
  thinkHarder?: boolean,
  surfaceId?: string,
  reasoningLevel?: ReasoningLevel,
  workspaceContext?: FintheonWorkspaceContext | null,
  agenticMode?: AgenticTaskMode,
) {
  const { conversationId, setConversationId, clearConversationId } =
    usePersistentFintheonConversation(agentId, surfaceId);

  const agentOverride = toFintheonAgentOverride(agentId);
  const connectorScopeId = conversationId
    ? `conversation:${conversationId}`
    : `surface:${surfaceId ?? agentId}`;
  const chat = useFintheonChat(
    conversationId,
    setConversationId,
    agentOverride,
    thinkHarder,
    clearConversationId,
    reasoningLevel,
    surfaceId,
    workspaceContext,
    agenticMode,
    connectorScopeId,
  );

  // Build chatHelpers compatible with UseChatHelpers from @ai-sdk/react
  const chatHelpers = {
    ...chat,
    id: agentId,
    error: undefined as Error | undefined,
    stop: async () => {
      chat.stop();
    },
  };
  const runtime = useOpenAgentsRuntime(chatHelpers);

  return {
    runtime,
    conversationId,
    setConversationId,
    clearConversationId,
    connectorScopeId,
    lastError: chat.lastError,
    clearError: chat.clearError,
    lastRequestId: chat.lastRequestId ?? null,
    lastAgenticTaskId: chat.lastAgenticTaskId ?? null,
    pausedTaskId: chat.pausedTaskId ?? null,
    pausingTaskId: chat.pausingTaskId ?? null,
    resumeActiveRun: chat.resumeActiveRun,
    queueActiveRun: chat.queueActiveRun,
    steerActiveRun: chat.steerActiveRun,
    updateQueuedRunMessage: chat.updateQueuedRunMessage,
    removeQueuedRunMessage: chat.removeQueuedRunMessage,
    addToolApprovalResponse: chat.addToolApprovalResponse,
    isLoading: chat.isLoading,
    thinkingContent: chat.thinkingContent,
  };
}
